import React, { ComponentProps } from 'react';

import { BearingEnvelope, Point2D as Point2DInterface, RoofPlane } from '@paragon/contract-library';
import { Direction2D, Point2D } from '@paragon/geometry-library';
import { getPlaneWithGeometry } from '@paragon/layout';

import { useAppSelector } from '../../../components/hooks';
import { LayoutCoordinateVisuals } from '../../../library/layout-geometry/LayoutCoordinateVisuals';
import { getInsertingBearingEnvelopeSettingsOrNull } from '../../../store/selectors/bearingEnvelopeInsertionSelectors';
import { getLayoutPointPickingTarget } from '../../../store/selectors/pickingSelectors';
import { InsertionBearingEnvelope } from '../../../store/slices/applicationSlice';
import { PointSlopeLineInsertionDimension } from '../../dimension/lines/PointSlopeLineInsertionDimension';
import { OptimizedDistanceDimensionContainer } from '../../dimension/OptimizedDistanceDimension';
import { useAutoInvalidateOnChange } from '../../hooks/invalidation-hooks';
import { usePointPickingSnapContext } from '../PointPickingSnapContext';
import { PlaneVisual } from '../roof-pieces/planes/PlaneVisual';
import { BearingEnvelopeDimension } from './BearingEnvelopeDimension';
import { BearingEnvelopeVisual } from './BearingEnvelopeVisual';
import { createPreviewBearingEnvelope } from './createPreviewBearingEnvelope';

const PREVIEW_ROOF_PLANE_GUID = 'bearing-envelope-insertion-roof-plane-preview';

export function BearingEnvelopeInsertionController(props: { threeDimensional: boolean }) {
  const pickingTarget = useAppSelector(getLayoutPointPickingTarget);

  const insertionEnvelope = useAppSelector((state) => {
    if (state.application.insertingObjectInfo?.type === 'bearingEnvelope') {
      return state.application.insertingObjectInfo.data;
    }
  });
  const insertionSettings = useAppSelector(getInsertingBearingEnvelopeSettingsOrNull);

  if (insertionEnvelope == null || insertionSettings == null) return null;

  const { leftPoint, rightPoint } = insertionEnvelope;

  if (leftPoint && rightPoint && !pickingTarget?.startsWith('bearing-envelope')) {
    return (
      <InsertionBearingEnvelopeVisual
        style="emphasised"
        threeDimensional={props.threeDimensional}
        interactive={false}
        dashed
        envelope={createPreviewBearingEnvelope(insertionEnvelope)}
        insertRoofPlane={insertionSettings.insertRoofPlane}
      />
    );
  }

  return (
    <PointSelectionModePreviewVisual
      envelope={insertionEnvelope}
      insertRoofPlane={insertionSettings.insertRoofPlane}
      threeDimensional={props.threeDimensional}
    />
  );
}

function PointSelectionModePreviewVisual(props: {
  envelope: InsertionBearingEnvelope;
  insertRoofPlane: boolean;
  threeDimensional: boolean;
}) {
  const pickingTarget = useAppSelector(getLayoutPointPickingTarget);
  const snapCtx = usePointPickingSnapContext();

  if (snapCtx?.snapResult == null) return null;

  const snappedPoint = snapCtx.snapResult.point;
  const { leftPoint, rightPoint } = props.envelope;

  const side = (() => {
    if (leftPoint && pickingTarget === 'bearing-envelope-right') return 'right';
    if (rightPoint && pickingTarget === 'bearing-envelope-left') return 'left';
    return null;
  })();
  if (side == null) return null;

  const previewPoint = { [`${side}Point`]: snappedPoint };
  const fixedPoint = side === 'right' ? leftPoint : rightPoint;

  return (
    <>
      <InsertionBearingEnvelopeVisual
        style="emphasised"
        hideLabelOnEnd={side}
        threeDimensional={props.threeDimensional}
        interactive={false}
        dashed
        envelope={createPreviewBearingEnvelope(props.envelope, previewPoint)}
        insertRoofPlane={props.insertRoofPlane}
      />

      {fixedPoint != null && <BearingEnvelopeInsertionAngleDimension basePoint={fixedPoint} endPoint={snappedPoint} />}
    </>
  );
}

function BearingEnvelopeInsertionAngleDimension(props: { basePoint: Point2DInterface; endPoint: Point2D }) {
  const basePoint = Point2D.fromObject(props.basePoint);
  if (basePoint.equals(props.endPoint)) return null;

  const direction = Direction2D.fromPoints(basePoint, props.endPoint);
  if (direction.isHorizontal() || direction.isVertical()) return null;

  return (
    <LayoutCoordinateVisuals>
      <PointSlopeLineInsertionDimension
        basePoint={props.basePoint}
        mousePosition={props.endPoint}
        showPitchInLabel={false}
      />
    </LayoutCoordinateVisuals>
  );
}

function InsertionBearingEnvelopeVisual(
  props: ComponentProps<typeof BearingEnvelopeVisual> & { readonly insertRoofPlane: boolean },
) {
  const { insertRoofPlane, ...visualProps } = props;

  return (
    <>
      <BearingEnvelopeVisual {...visualProps} />
      <BearingEnvelopeRoofPlanePreviewVisual envelope={props.envelope} visible={insertRoofPlane} />

      <OptimizedDistanceDimensionContainer threeDimensional={props.threeDimensional}>
        <BearingEnvelopeDimension envelope={props.envelope} />
      </OptimizedDistanceDimensionContainer>
    </>
  );
}

function BearingEnvelopeRoofPlanePreviewVisual(props: { envelope: BearingEnvelope; visible: boolean }) {
  const defaultProperties = useAppSelector((state) => state.layout.roofPlaneInsertionSettings.defaultProperties);

  useAutoInvalidateOnChange(props.visible, defaultProperties, props.envelope);

  if (!props.visible) return null;

  const roofPlane = {
    guid: PREVIEW_ROOF_PLANE_GUID,
    ...defaultProperties,
    flipped: false,
    geometry: { type: 'BearingEnvelope', bearingEnvelopeGuid: props.envelope.guid },
    levelGuid: props.envelope.levelGuid,
    cuts: [],
  } satisfies RoofPlane;

  const calculatedRoofPlane = getPlaneWithGeometry(roofPlane, { [props.envelope.guid]: props.envelope });
  if (calculatedRoofPlane == null) return null;

  return (
    <LayoutCoordinateVisuals>
      <PlaneVisual style="roof.emphasised" mode="translucent" plane={calculatedRoofPlane} pickable={false} />
    </LayoutCoordinateVisuals>
  );
}
